/**
 * TechCheck -- mic + camera permission flow with live preview.
 *
 * Used inside the InterviewRoom lobby before the candidate clicks "Start".
 * Apple-clean B2B styling consistent with the rest of the recruiter UI.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Camera,
  Mic,
  Volume2,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  RefreshCcw,
  ChevronDown,
  Monitor,
} from 'lucide-react';

type PermState = 'idle' | 'requesting' | 'granted' | 'denied' | 'unsupported';

export interface TechCheckResult {
  audio: boolean;
  video: boolean;
  /**
   * Whether the candidate has shared (or was not required to share) their
   * screen. The parent treats this as the gate for the Start button.
   */
  screen: boolean;
}

interface TechCheckProps {
  language: 'de' | 'en';
  onReady: (result: TechCheckResult) => void;
  /**
   * Hand the acquired MediaStream to the parent. After this fires, the parent
   * owns the stream and is responsible for stopping it (so we can reuse the
   * same camera/mic in the call stage without re-prompting the user).
   */
  onStreamReady?: (stream: MediaStream) => void;
  /**
   * Hand the screen-share MediaStream to the parent (or null if the user
   * revoked it). The recorder pipeline mixes this with the camera into a
   * single composite WebM.
   */
  onScreenStreamReady?: (stream: MediaStream | null) => void;
  /**
   * When true, the candidate MUST share their screen before the start
   * button unlocks (hard gate set by the recruiter's template).
   */
  requireScreenShare?: boolean;
  /**
   * When true, the screen-share section is rendered even if not
   * required, so the candidate CAN share but isn't forced to. Used by
   * mock interviews where the template normally wants a screen track
   * for review, but mobile users (no `getDisplayMedia` on iOS) should
   * still be able to start. Defaults to `requireScreenShare` so
   * existing call sites are unaffected.
   */
  offerScreenShare?: boolean;
  className?: string;
}

const TechCheck: React.FC<TechCheckProps> = ({
  language,
  onReady,
  onStreamReady,
  onScreenStreamReady,
  requireScreenShare = false,
  offerScreenShare,
  className,
}) => {
  const showScreenShare = offerScreenShare ?? requireScreenShare;
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const ownsStreamRef = useRef(true);
  const audioCleanupRef = useRef<(() => void) | null>(null);

  // Screen-share refs / state (only used when requireScreenShare is on).
  const screenStreamRef = useRef<MediaStream | null>(null);
  const screenVideoRef = useRef<HTMLVideoElement | null>(null);
  const [screenPermState, setScreenPermState] = useState<PermState>('idle');
  const [screenError, setScreenError] = useState<string | null>(null);

  const [permState, setPermState] = useState<PermState>('idle');
  const [audioLevel, setAudioLevel] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // iOS Safari does not implement getDisplayMedia at all. We detect this
  // up-front so we can show a friendly "please use a desktop browser"
  // hint instead of waiting for the user to click and getting a generic
  // error from the underlying API.
  const screenShareSupported = useMemo(() => {
    if (typeof navigator === 'undefined') return false;
    return (
      !!navigator.mediaDevices &&
      typeof (navigator.mediaDevices as MediaDevices).getDisplayMedia === 'function'
    );
  }, []);

  // Device picker state. We don't enumerate devices until permission was
  // granted at least once -- some browsers (esp. Safari) return empty
  // labels otherwise, making the picker useless.
  const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([]);
  const [audioDevices, setAudioDevices] = useState<MediaDeviceInfo[]>([]);
  const [videoDeviceId, setVideoDeviceId] = useState<string | null>(null);
  const [audioDeviceId, setAudioDeviceId] = useState<string | null>(null);
  const [switching, setSwitching] = useState(false);

  const t = useMemo(
    () => ({
      title:  language === 'de' ? 'Tech-Check' : 'Tech check',
      sub:    language === 'de'
        ? 'Prüfe Mikrofon, Kamera und Bildschirm, bevor du startest.'
        : 'Test mic, camera and screen before you start.',
      enable: language === 'de'
        ? (requireScreenShare ? 'Mikrofon, Kamera & Bildschirm freigeben' : 'Geräte freigeben')
        : (requireScreenShare ? 'Allow mic, camera & screen' : 'Allow camera + mic'),
      screenOptionalLabel: language === 'de' ? 'Bildschirm teilen (optional)' : 'Share screen (optional)',
      screenOptionalWhy: language === 'de'
        ? 'Optional fürs Recording. Hilfreich wenn du Notizen, Code oder ein Whiteboard offen hast - überspring es einfach wenn du am Handy bist oder nichts teilen willst.'
        : 'Optional for the recording. Useful if you have notes, code or a whiteboard open - skip it if you are on mobile or do not want to share.',
      cameraOk: language === 'de' ? 'Kamera bereit' : 'Camera ready',
      micOk:    language === 'de' ? 'Mikrofon bereit' : 'Microphone ready',
      noPerm:   language === 'de'
        ? 'Browser-Zugriff verweigert. Erlaube Kamera + Mikrofon in den Browser-Einstellungen und versuche es erneut.'
        : 'Browser permission denied. Allow camera + mic in your browser settings and retry.',
      unsupported: language === 'de'
        ? 'Dein Browser unterstützt MediaRecorder nicht. Bitte nutze Chrome, Firefox, Edge oder Safari (aktuell).'
        : 'Your browser does not support MediaRecorder. Please use a recent version of Chrome, Firefox, Edge, or Safari.',
      retry:    language === 'de' ? 'Erneut versuchen' : 'Try again',
      ready:    language === 'de' ? 'Alles bereit' : 'All ready',
      checking: language === 'de' ? 'Prüfe Geräte ...' : 'Checking devices ...',
      audioHint: language === 'de'
        ? 'Sprich kurz - du solltest den Balken sehen.'
        : 'Say something - the bar should react.',
      cameraLabel: language === 'de' ? 'Kamera' : 'Camera',
      micLabel:    language === 'de' ? 'Mikrofon' : 'Microphone',
      defaultDev:  language === 'de' ? 'Standardgerät' : 'Default device',
      screenLabel: language === 'de' ? 'Bildschirm' : 'Screen',
      screenOk:    language === 'de' ? 'Bildschirm wird geteilt' : 'Screen is being shared',
      screenStart: language === 'de' ? 'Bildschirm freigeben' : 'Share screen',
      screenStop:  language === 'de' ? 'Bildschirm wechseln' : 'Change screen',
      screenWhy:   language === 'de'
        ? 'Dieses Interview erfordert das Teilen deines Bildschirms. So sehen Recruiter dein Coding-Setup, Whiteboard oder die Aufgabenstellung im Recording. Schließe sensible Tabs/Apps bevor du startest.'
        : 'This interview requires sharing your screen so the recruiter can see your coding setup, whiteboard or task in the recording. Close any sensitive tabs/apps before you start.',
      screenDenied: language === 'de'
        ? 'Bildschirmfreigabe wurde abgelehnt. Bitte erneut versuchen.'
        : 'Screen share was denied. Please try again.',
      screenUnsupported: language === 'de'
        ? 'Bildschirmfreigabe wird auf diesem Gerät nicht unterstützt. Bitte nutze einen Desktop- oder Laptop-Browser (Chrome, Firefox, Edge, Safari).'
        : 'Screen sharing is not supported on this device. Please use a desktop or laptop browser (Chrome, Firefox, Edge, Safari).',
      screenHint: language === 'de'
        ? 'Tipp: Wähle "Gesamter Bildschirm" für die beste Sicht auf dein Setup.'
        : 'Tip: pick "Entire screen" for the best view of your setup.',
    }),
    [language, requireScreenShare],
  );

  const stopCurrentStream = useCallback(() => {
    if (audioCleanupRef.current) {
      try { audioCleanupRef.current(); } catch { /* noop */ }
      audioCleanupRef.current = null;
    }
    if (streamRef.current && ownsStreamRef.current) {
      streamRef.current.getTracks().forEach((tr) => tr.stop());
    }
    streamRef.current = null;
  }, []);

  const attachAudioMeter = useCallback((stream: MediaStream) => {
    try {
      const AudioCtx: typeof AudioContext =
        (window as any).AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      src.connect(analyser);
      const buf = new Uint8Array(analyser.frequencyBinCount);
      let stopped = false;

      const tick = () => {
        if (stopped || !streamRef.current) return;
        analyser.getByteTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i++) {
          const v = (buf[i] - 128) / 128;
          sum += v * v;
        }
        const rms = Math.sqrt(sum / buf.length);
        setAudioLevel(Math.min(1, rms * 3));
        requestAnimationFrame(tick);
      };
      tick();
      audioCleanupRef.current = () => {
        stopped = true;
        try { ctx.close(); } catch { /* noop */ }
      };
    } catch (e) {
      console.warn('audio-meter init failed', e);
    }
  }, []);

  const enumerateDevices = useCallback(async () => {
    try {
      const list = await navigator.mediaDevices.enumerateDevices();
      setVideoDevices(list.filter((d) => d.kind === 'videoinput'));
      setAudioDevices(list.filter((d) => d.kind === 'audioinput'));
    } catch (e) {
      console.warn('enumerateDevices failed', e);
    }
  }, []);

  /**
   * Acquire (or re-acquire) the media stream. If `videoDeviceId` /
   * `audioDeviceId` are set we honor them via `deviceId.exact`; otherwise
   * we use the browser's default. Calling this while we already have a
   * stream tears the old one down first so we never leak tracks.
   */
  const acquire = useCallback(
    async (opts: { videoId?: string | null; audioId?: string | null }) => {
      setError(null);
      if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
        setPermState('unsupported');
        return;
      }
      stopCurrentStream();
      setSwitching(true);
      const constraints: MediaStreamConstraints = {
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          // Explicit autoGainControl so we don't rely on browser
          // defaults (Chrome ships it on, Safari on macOS leaves it
          // off). Without AGC, low-volume candidates produce mic
          // signal too quiet for Azure's server VAD to recognise
          // as continuous speech -- so the AI cuts back in too early
          // -- and loud transients (clicks, scrolls) stand out
          // disproportionately and trip the VAD as fake barge-ins.
          autoGainControl: true,
          ...(opts.audioId ? { deviceId: { exact: opts.audioId } } : {}),
        },
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          facingMode: 'user',
          ...(opts.videoId ? { deviceId: { exact: opts.videoId } } : {}),
        },
      };
      try {
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        streamRef.current = stream;
        ownsStreamRef.current = true;
        attachAudioMeter(stream);
        setPermState('granted');
        // We only consider the gate "passed" when screen share is also OK
        // (or not required at all). The parent reads this aggregated
        // boolean to decide whether the Start button unlocks.
        const screenOk = !requireScreenShare || !!screenStreamRef.current;
        onReady({ audio: true, video: true, screen: screenOk });
        if (onStreamReady) {
          ownsStreamRef.current = false;
          onStreamReady(stream);
        }
        // Once we have permission, device labels are populated -- enumerate
        // again so the dropdowns show readable names.
        void enumerateDevices();
        const v = stream.getVideoTracks()[0]?.getSettings().deviceId || null;
        const a = stream.getAudioTracks()[0]?.getSettings().deviceId || null;
        if (v) setVideoDeviceId(v);
        if (a) setAudioDeviceId(a);
      } catch (e: any) {
        const msg = e?.message || String(e);
        if (e?.name === 'NotAllowedError' || e?.name === 'PermissionDeniedError') {
          setPermState('denied');
        } else {
          setPermState('denied');
          setError(msg);
        }
      } finally {
        setSwitching(false);
      }
    },
    [attachAudioMeter, enumerateDevices, onReady, onStreamReady, requireScreenShare, stopCurrentStream],
  );

  // ----------------------------------------------------------------
  // Screen-share acquisition
  // ----------------------------------------------------------------

  // True while THIS component is responsible for stopping the screen
  // tracks (during the lobby preview). Flipped to false the moment we
  // hand the stream off to the parent via `onScreenStreamReady` -- from
  // then on the parent (InterviewRoom) keeps the stream alive for the
  // actual recording and is responsible for cleanup at session end.
  //
  // Without this, the cleanup effect that runs when TechCheck unmounts
  // (user clicks "Start interview" -> Lobby unmounts) would stop()
  // every track, leaving the parent holding a stream of dead tracks
  // and the recording canvas filled with a single black frame.
  const ownsScreenRef = useRef(true);

  const stopScreenStream = useCallback(() => {
    if (screenStreamRef.current && ownsScreenRef.current) {
      try { screenStreamRef.current.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ }
    }
    screenStreamRef.current = null;
    ownsScreenRef.current = true;
    if (screenVideoRef.current) {
      try { screenVideoRef.current.srcObject = null; } catch { /* ignore */ }
    }
  }, []);

  const acquireScreen = useCallback(async () => {
    setScreenError(null);
    if (!screenShareSupported) {
      setScreenPermState('unsupported');
      return;
    }
    setScreenPermState('requesting');
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        // We deliberately do NOT request audio from the screen because
        // (a) most setups can't deliver tab/system audio reliably and
        // (b) it risks double-capturing the candidate's mic. The
        // interview's audio mix is already perfect (mic + AI playback).
        video: { frameRate: { ideal: 15, max: 30 } },
        audio: false,
      } as DisplayMediaStreamOptions);
      // Listen for user pressing the browser's "stop sharing" button.
      const track = stream.getVideoTracks()[0];
      if (track) {
        track.addEventListener('ended', () => {
          stopScreenStream();
          setScreenPermState('idle');
          if (onScreenStreamReady) onScreenStreamReady(null);
          // Re-check the aggregate gate -- if screen was required, the
          // start button needs to lock again. Use streamRef (not
          // permState) to avoid the same stale-closure issue as above:
          // by the time 'ended' fires, the closure here is from when
          // acquireScreen was defined, which may not reflect the current
          // mic/camera state.
          const haveCamMic = !!streamRef.current;
          onReady({
            audio: haveCamMic,
            video: haveCamMic,
            screen: !requireScreenShare,
          });
        });
      }
      screenStreamRef.current = stream;
      // Attach to preview <video> so the candidate sees what they shared.
      if (screenVideoRef.current) {
        try { screenVideoRef.current.srcObject = stream; } catch { /* ignore */ }
        screenVideoRef.current.play().catch(() => { /* autoplay ok */ });
      }
      setScreenPermState('granted');
      if (onScreenStreamReady) {
        // Hand stream ownership over to the parent. After this point
        // TechCheck must NOT call .stop() on the tracks (e.g. from the
        // unmount cleanup effect) -- the parent owns the lifecycle now.
        ownsScreenRef.current = false;
        onScreenStreamReady(stream);
      }
      // If camera was already granted, re-fire onReady so the parent
      // updates its `canStart` state to true.
      // IMPORTANT: read from streamRef (not permState) -- this callback's
      // closure captures `permState` from the render that defined it,
      // which is usually 'idle' or 'requesting' when start() calls us
      // 150ms after acquire(). The actual camera/mic stream is set on
      // streamRef synchronously inside acquire(), so the ref is fresh
      // and unaffected by React's batched re-renders.
      if (streamRef.current) {
        onReady({ audio: true, video: true, screen: true });
      }
    } catch (e) {
      const name = (e as { name?: string })?.name;
      if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
        setScreenPermState('denied');
      } else if (name === 'NotSupportedError') {
        setScreenPermState('unsupported');
      } else {
        setScreenPermState('denied');
        setScreenError(e instanceof Error ? e.message : String(e));
      }
      if (onScreenStreamReady) onScreenStreamReady(null);
    }
  }, [
    onReady,
    onScreenStreamReady,
    requireScreenShare,
    screenShareSupported,
    stopScreenStream,
  ]);

  // Cleanup the screen stream on unmount.
  useEffect(() => {
    return () => stopScreenStream();
  }, [stopScreenStream]);

  const start = useCallback(async () => {
    setPermState('requesting');
    await acquire({ videoId: videoDeviceId, audioId: audioDeviceId });
    // After cam+mic are granted, chain straight into the screen-share
    // prompt so the candidate sees one continuous "allow stuff" flow
    // instead of two disconnected interactions.
    //
    // We trigger this whenever screen share is OFFERED (required OR
    // optional), not just when it's required. In optional mode (mocks)
    // the start button does not re-lock on denial -- acquireScreen()'s
    // catch sets screenPermState='denied' and onReady() reports
    // screen:true via `!requireScreenShare`, so the candidate can
    // proceed either way. Auto-prompting just removes the extra click
    // most users were missing.
    //
    // Skip on platforms without getDisplayMedia (iOS Safari) so we
    // don't show a "denied" state for an unsupported feature.
    const shouldChainScreen =
      showScreenShare &&
      screenShareSupported &&
      !!streamRef.current &&
      !screenStreamRef.current;
    if (shouldChainScreen) {
      // Tiny delay so the camera permission popup has fully closed before
      // we fire the next one -- Chrome/Edge are happier this way.
      setTimeout(() => {
        void acquireScreen();
      }, 150);
    }
  }, [
    acquire,
    acquireScreen,
    audioDeviceId,
    screenShareSupported,
    showScreenShare,
    videoDeviceId,
  ]);

  // When the user picks a different camera or mic, re-acquire only if we
  // already have a granted stream. We don't trigger an acquisition on the
  // initial render -- the user has to click "Enable" first.
  const handleVideoChange = useCallback(
    (id: string) => {
      setVideoDeviceId(id || null);
      if (permState === 'granted') void acquire({ videoId: id || null, audioId: audioDeviceId });
    },
    [acquire, audioDeviceId, permState],
  );
  const handleAudioChange = useCallback(
    (id: string) => {
      setAudioDeviceId(id || null);
      if (permState === 'granted') void acquire({ videoId: videoDeviceId, audioId: id || null });
    },
    [acquire, permState, videoDeviceId],
  );

  // Subscribe to device-list changes (USB headset plugged in mid-session)
  useEffect(() => {
    if (!navigator.mediaDevices?.addEventListener) return;
    const onChange = () => { void enumerateDevices(); };
    navigator.mediaDevices.addEventListener('devicechange', onChange);
    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', onChange);
    };
  }, [enumerateDevices]);

  useEffect(() => {
    return () => stopCurrentStream();
  }, [stopCurrentStream]);

  // Attach the captured MediaStream to the <video> element AFTER React has
  // mounted it (once permState flips to 'granted'). Assigning srcObject
  // before the element exists is the classic "I clicked allow but I dont
  // see myself" bug.
  useEffect(() => {
    if (permState !== 'granted') return;
    const el = videoRef.current;
    const stream = streamRef.current;
    if (!el || !stream) return;
    if (el.srcObject !== stream) {
      el.srcObject = stream;
    }
    const tryPlay = () => {
      el.play().catch(() => { /* autoplay restrictions ok */ });
    };
    if (el.readyState >= 1) {
      tryPlay();
    } else {
      el.onloadedmetadata = tryPlay;
    }
  }, [permState]);

  // Same idea for the screen-share preview: the <video ref={screenVideoRef}>
  // is rendered conditionally on `screenPermState === 'granted'`, so the
  // ref is null when we first try to assign srcObject inside acquireScreen.
  // This effect runs AFTER the element mounts and wires the stream up.
  useEffect(() => {
    if (screenPermState !== 'granted') return;
    const el = screenVideoRef.current;
    const stream = screenStreamRef.current;
    if (!el || !stream) return;
    if (el.srcObject !== stream) {
      el.srcObject = stream;
    }
    const tryPlay = () => {
      el.play().catch(() => { /* autoplay restrictions ok */ });
    };
    if (el.readyState >= 1) {
      tryPlay();
    } else {
      el.onloadedmetadata = tryPlay;
    }
  }, [screenPermState]);

  return (
    <div className={className || ''}>
      <div className="rounded-3xl bg-white border border-slate-200/70 overflow-hidden shadow-[0_2px_8px_rgba(15,23,42,0.04)]">
        <div className="px-5 py-4 border-b border-slate-100">
          <h3 className="text-base font-semibold text-slate-900 tracking-tight">{t.title}</h3>
          <p className="text-xs text-slate-500 mt-0.5">{t.sub}</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-[1fr_240px] gap-0">
          {/* Camera preview */}
          <div className="relative bg-slate-950 aspect-video sm:aspect-auto sm:min-h-[200px] flex items-center justify-center overflow-hidden">
            {permState === 'granted' ? (
              <video
                ref={videoRef}
                muted
                playsInline
                autoPlay
                className="w-full h-full object-cover"
                style={{ transform: 'scaleX(-1)' }}
              />
            ) : permState === 'requesting' ? (
              <div className="flex flex-col items-center text-slate-300 text-sm gap-2">
                <Loader2 className="w-6 h-6 animate-spin" />
                {t.checking}
              </div>
            ) : (
              <div className="flex flex-col items-center text-slate-300 text-sm gap-2 px-6 text-center">
                <Camera className="w-7 h-7 opacity-70" />
                <p className="text-xs leading-relaxed">
                  {language === 'de'
                    ? 'Klicke unten, um Kamera + Mikrofon freizugeben.'
                    : 'Click below to enable camera + microphone.'}
                </p>
              </div>
            )}
          </div>

          {/* Status sidebar */}
          <div className="p-4 flex flex-col gap-3 bg-white">
            <StatusRow
              icon={<Camera className="w-4 h-4" />}
              label={t.cameraOk}
              ok={permState === 'granted'}
            />
            {permState === 'granted' && (
              <DeviceSelect
                label={t.cameraLabel}
                value={videoDeviceId || ''}
                options={videoDevices}
                onChange={handleVideoChange}
                disabled={switching}
                placeholder={t.defaultDev}
                kind="video"
              />
            )}
            <div>
              <StatusRow
                icon={<Mic className="w-4 h-4" />}
                label={t.micOk}
                ok={permState === 'granted'}
              />
              {permState === 'granted' && (
                <>
                  <div className="mt-2">
                    <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-pink-500 to-orange-400 transition-[width] duration-75"
                        style={{ width: `${Math.round(audioLevel * 100)}%` }}
                      />
                    </div>
                    <p className="text-[10px] text-slate-400 mt-1">{t.audioHint}</p>
                  </div>
                  <div className="mt-2">
                    <DeviceSelect
                      label={t.micLabel}
                      value={audioDeviceId || ''}
                      options={audioDevices}
                      onChange={handleAudioChange}
                      disabled={switching}
                      placeholder={t.defaultDev}
                      kind="audio"
                    />
                  </div>
                </>
              )}
            </div>
            <StatusRow
              icon={<Volume2 className="w-4 h-4" />}
              label={language === 'de' ? 'Lautsprecher (Browser-Standard)' : 'Speaker (browser default)'}
              ok={true}
              soft
            />
          </div>
        </div>

        {/* Screen-share section (rendered when required by the template,
            or when the parent has explicitly marked it as optional via
            `offerScreenShare` -- the latter is the mock-interview case
            where mobile users still need to be able to start without
            sharing their screen). */}
        {showScreenShare && (
          <div className="border-t border-slate-100 px-5 py-4 bg-slate-50/40">
            <div className="flex items-start gap-3">
              <div
                className={
                  screenPermState === 'granted'
                    ? 'w-8 h-8 rounded-lg flex items-center justify-center bg-emerald-50 text-emerald-600 flex-shrink-0'
                    : 'w-8 h-8 rounded-lg flex items-center justify-center bg-slate-100 text-slate-500 flex-shrink-0'
                }
              >
                <Monitor className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-medium text-slate-900">
                      {screenPermState === 'granted'
                        ? t.screenOk
                        : (requireScreenShare ? t.screenLabel : t.screenOptionalLabel)}
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
                      {screenPermState === 'unsupported' || !screenShareSupported
                        ? (requireScreenShare ? t.screenUnsupported : t.screenOptionalWhy)
                        : screenPermState === 'denied'
                          ? (screenError || t.screenDenied)
                          : screenPermState === 'granted'
                            ? t.screenHint
                            : (requireScreenShare ? t.screenWhy : t.screenOptionalWhy)}
                    </p>
                  </div>
                  {screenShareSupported && screenPermState !== 'unsupported' && (
                    <button
                      type="button"
                      onClick={acquireScreen}
                      disabled={screenPermState === 'requesting'}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full bg-slate-900 text-white hover:bg-pink-600 disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed transition-colors flex-shrink-0"
                    >
                      {screenPermState === 'requesting' ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : screenPermState === 'denied' ? (
                        <RefreshCcw className="w-3 h-3" />
                      ) : screenPermState === 'granted' ? (
                        <RefreshCcw className="w-3 h-3" />
                      ) : (
                        <Monitor className="w-3 h-3" />
                      )}
                      {screenPermState === 'granted' ? t.screenStop : t.screenStart}
                    </button>
                  )}
                </div>
                {/* Live thumbnail of the shared screen so the candidate
                    can verify they picked the right window. */}
                {screenPermState === 'granted' && (
                  <div className="mt-3 rounded-xl overflow-hidden bg-slate-950 border border-slate-200/70 aspect-video max-h-40">
                    <video
                      ref={screenVideoRef}
                      muted
                      playsInline
                      autoPlay
                      className="w-full h-full object-contain"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="px-5 py-4 border-t border-slate-100 flex items-center justify-between gap-3">
          {permState === 'granted' ? (
            <div className="flex items-center gap-2 text-sm text-emerald-600">
              <CheckCircle2 className="w-4 h-4" />
              {t.ready}
            </div>
          ) : permState === 'denied' ? (
            <div className="flex items-start gap-2 text-sm text-red-600">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{error || t.noPerm}</span>
            </div>
          ) : permState === 'unsupported' ? (
            <div className="flex items-start gap-2 text-sm text-amber-600">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{t.unsupported}</span>
            </div>
          ) : (
            <span className="text-xs text-slate-500">
              {language === 'de'
                ? 'Cevee speichert nur, was du explizit erlaubt hast.'
                : 'Cevee only records what you explicitly allowed.'}
            </span>
          )}

          {permState !== 'granted' && permState !== 'unsupported' && (
            <button
              onClick={start}
              disabled={permState === 'requesting'}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white rounded-full bg-slate-900 hover:bg-pink-600 disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed transition-colors"
            >
              {permState === 'denied' ? <RefreshCcw className="w-3.5 h-3.5" /> : null}
              {permState === 'denied' ? t.retry : t.enable}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const DeviceSelect: React.FC<{
  label: string;
  value: string;
  options: MediaDeviceInfo[];
  onChange: (id: string) => void;
  disabled?: boolean;
  placeholder?: string;
  kind: 'video' | 'audio';
}> = ({ label, value, options, onChange, disabled, placeholder, kind }) => {
  if (options.length <= 1) return null;
  return (
    <div className="relative">
      <label className="block text-[10px] font-medium uppercase tracking-wider text-slate-400 mb-1">
        {label}
      </label>
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className="w-full appearance-none rounded-lg border border-slate-200 bg-white pl-2.5 pr-7 py-1.5 text-xs text-slate-700 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-pink-400/30 focus:border-pink-400 disabled:opacity-50 disabled:cursor-not-allowed truncate"
        >
          {!value && <option value="">{placeholder}</option>}
          {options.map((d) => (
            <option key={d.deviceId} value={d.deviceId}>
              {d.label || (kind === 'video' ? 'Camera' : 'Microphone')}
            </option>
          ))}
        </select>
        <ChevronDown className="w-3 h-3 text-slate-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
      </div>
    </div>
  );
};

const StatusRow: React.FC<{
  icon: React.ReactNode;
  label: string;
  ok: boolean;
  soft?: boolean;
}> = ({ icon, label, ok, soft }) => (
  <div className="flex items-center gap-2 text-sm">
    <div
      className={
        ok
          ? 'w-7 h-7 rounded-lg flex items-center justify-center bg-emerald-50 text-emerald-600 flex-shrink-0'
          : 'w-7 h-7 rounded-lg flex items-center justify-center bg-slate-50 text-slate-400 flex-shrink-0'
      }
    >
      {icon}
    </div>
    <span className={ok ? 'text-slate-800' : soft ? 'text-slate-500' : 'text-slate-500'}>{label}</span>
    {ok && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 ml-auto flex-shrink-0" />}
  </div>
);

export default TechCheck;
